import { ProviderPaymentDateRuleUpsertDTO, StrategyCode, TriggerSource } from '../../types';

const isInt = (v: unknown) => Number.isInteger(Number(v));
const toNum = (v: any): number => (v === '' || v === null || v === undefined ? NaN : Number(v));
export const isIntAny = (v: unknown): boolean => {
	if (v === '' || v === null || v === undefined) return false;
	const n = Number(v);
	return Number.isInteger(n);
};

export const allowNegativeNetDays = (triggerSource?: TriggerSource): boolean => {
	return triggerSource === TriggerSource.CheckIn || triggerSource === TriggerSource.CheckOut;
};

export function validateUpsert(dto: ProviderPaymentDateRuleUpsertDTO): Record<string, string> {
	const errs: Record<string, string> = {};
	const cfg: any = dto.configuration;

	// triggerSource ya viene validado por select (provider-only)
	switch (dto.strategyCode) {
		case StrategyCode.NetDays: {
			const days = toNum(cfg.days);
			const ts = cfg.triggerSource as TriggerSource | undefined;

			if (!isInt(days)) {
				errs['configuration.days'] = 'days debe ser un entero';
				break;
			}

			// ServiceCreation => >= 0
			if (!allowNegativeNetDays(ts) && days < 0) {
				errs['configuration.days'] = 'days debe ser un entero >= 0';
			}

			break;
		}

		case StrategyCode.WeeklyRelative: {
			const weeksOffset = toNum(cfg.weeksOffset);
			if (!isInt(weeksOffset) || weeksOffset < 0)
				errs['configuration.weeksOffset'] = 'weeksOffset debe ser un entero >= 0';

			if (!cfg.dayOfWeek) errs['configuration.dayOfWeek'] = 'dayOfWeek es requerido';
			break;
		}

		case StrategyCode.MonthlyRelative: {
			const monthOffset = toNum(cfg.monthOffset);
			if (!isInt(monthOffset) || monthOffset < 0)
				errs['configuration.monthOffset'] = 'monthOffset debe ser un entero >= 0';

			const weekOrdinal = toNum(cfg.weekOrdinal);
			const okOrdinal = weekOrdinal === -1 || (weekOrdinal >= 1 && weekOrdinal <= 5);
			if (!isInt(weekOrdinal) || !okOrdinal)
				errs['configuration.weekOrdinal'] = 'weekOrdinal debe ser -1 (último) o 1..5';

			if (!cfg.dayOfWeek) errs['configuration.dayOfWeek'] = 'dayOfWeek es requerido';
			break;
		}

		case StrategyCode.SplitMonth: {
			const cutoffDay = toNum(cfg.cutoffDay);
			if (!isInt(cutoffDay) || cutoffDay < 1 || cutoffDay > 31)
				errs['configuration.cutoffDay'] = 'cutoffDay debe estar entre 1 y 31';

			const fp = cfg.firstPeriod || {};
			const sp = cfg.secondPeriod || {};

			const fpFixed = toNum(fp.fixedDay);
			if (!isInt(fpFixed) || fpFixed < 1 || fpFixed > 31)
				errs['configuration.firstPeriod.fixedDay'] = 'firstPeriod.fixedDay debe estar entre 1 y 31';

			const fpMo = toNum(fp.monthOffset);
			if (!isInt(fpMo) || fpMo < 0)
				errs['configuration.firstPeriod.monthOffset'] = 'firstPeriod.monthOffset debe ser >= 0';

			const spFixed = toNum(sp.fixedDay);
			if (!isInt(spFixed) || spFixed < 1 || spFixed > 31)
				errs['configuration.secondPeriod.fixedDay'] = 'secondPeriod.fixedDay debe estar entre 1 y 31';

			const spMo = toNum(sp.monthOffset);
			if (!isInt(spMo) || spMo < 0)
				errs['configuration.secondPeriod.monthOffset'] = 'secondPeriod.monthOffset debe ser >= 0';

			break;
		}

		case StrategyCode.CustomCalendar: {
			const periods = Array.isArray(cfg.periods) ? cfg.periods : [];
			if (periods.length === 0) errs['configuration.periods'] = 'periods no puede estar vacío';

			periods.forEach((p: any, idx: number) => {
				if (!p?.from) errs[`configuration.periods.${idx}.from`] = 'from es requerido';
				if (!p?.to) errs[`configuration.periods.${idx}.to`] = 'to es requerido';
				if (!p?.dueDate) errs[`configuration.periods.${idx}.dueDate`] = 'dueDate es requerido';
			});

			const fb = cfg.fallbackStrategy;
			if (!fb?.strategyCode)
				errs['configuration.fallbackStrategy.strategyCode'] = 'fallbackStrategy.strategyCode es requerido';
			if (fb?.strategyCode === StrategyCode.CustomCalendar)
				errs['configuration.fallbackStrategy.strategyCode'] = 'CustomCalendar no puede usarse como fallback';

			// validaciones del fallback (subset)
			if (fb?.strategyCode === StrategyCode.NetDays) {
				const days = toNum(fb.days);
				const ts = cfg.triggerSource as TriggerSource | undefined;

				if (!isInt(days)) {
					errs['configuration.fallbackStrategy.days'] = 'days debe ser un entero';
				} else if (!allowNegativeNetDays(ts) && days < 0) {
					errs['configuration.fallbackStrategy.days'] = 'days debe ser un entero >= 0';
				}
			}

			if (fb?.strategyCode === StrategyCode.WeeklyRelative) {
				const w = toNum(fb.weeksOffset);
				if (!isInt(w) || w < 0) errs['configuration.fallbackStrategy.weeksOffset'] = 'weeksOffset debe ser >= 0';
				if (!fb.dayOfWeek) errs['configuration.fallbackStrategy.dayOfWeek'] = 'dayOfWeek es requerido';
			}

			if (fb?.strategyCode === StrategyCode.MonthlyRelative) {
				const mo = toNum(fb.monthOffset);
				if (!isInt(mo) || mo < 0) errs['configuration.fallbackStrategy.monthOffset'] = 'monthOffset debe ser >= 0';

				const wo = toNum(fb.weekOrdinal);
				const okOrdinal = wo === -1 || (wo >= 1 && wo <= 5);
				if (!isInt(wo) || !okOrdinal)
					errs['configuration.fallbackStrategy.weekOrdinal'] = 'weekOrdinal debe ser -1 o 1..5';

				if (!fb.dayOfWeek) errs['configuration.fallbackStrategy.dayOfWeek'] = 'dayOfWeek es requerido';
			}

			if (fb?.strategyCode === StrategyCode.SplitMonth) {
				const cutoff = toNum(fb.cutoffDay);
				if (!isInt(cutoff) || cutoff < 1 || cutoff > 31)
					errs['configuration.fallbackStrategy.cutoffDay'] = 'cutoffDay debe estar entre 1 y 31';
			}
			break;
		}

		default:
			break;
	}

	return errs;
}
